import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  CircularProgress,
  Stack,
  FormHelperText,
  Box,
} from '@mui/material';
import { getJwtTokenFromIdToken } from '../auth/AuthFanc';
import { Task, UploadFile } from '@mui/icons-material';
import { fetchErrorText } from '../../../common/const';

const FIRMWARE_FILEDATA_LEN_LIMIT = 5 * 1000 * 1000;
const FIRMWARE_FILEDATA_LEN_LIMIT_TEXT = '3.75 MB';
const FIRMWARE_FILENAME_LEN_LIMIT = 127;
const FIRMWARE_MEMO_LEN_LIMIT = 127;

const SNACKBAR_TEXT_REGISTER_SUCCESS = 'ファームウェアを登録しました。';

interface FirmwareUploadModalProps {
  open: boolean;
  close: (registered: any | null) => void;
  openSnackBarWithSuccess: (text: string) => void;
  openSnackBarWithError: (text: string) => void;
}

const FirmwareUploadModal: React.FC<FirmwareUploadModalProps> = (
  props: FirmwareUploadModalProps
) => {
  const [fileData, setFileData] = React.useState<string>('');
  const [fileName, setFileName] = React.useState<string>('');
  const [memo, setMemo] = React.useState<string>('');

  const [validationErrors, setValidationErrors] = React.useState({
    fileData: '',
    fileName: '',
    memo: '',
  });

  const [isFileLoadInProgress, setIsFileLoadInProgress] =
    React.useState<boolean>(false);

  const [isSubmitInProgress, setIsSubmitInProgress] =
    React.useState<boolean>(false);

  const uploadInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleValidation = () => {
    const errors = {
      fileData: !fileData
        ? 'ファイルを指定してください。'
        : fileData.length > FIRMWARE_FILEDATA_LEN_LIMIT
        ? `${FIRMWARE_FILEDATA_LEN_LIMIT_TEXT} 以下のファイルを指定してください。`
        : '',
      fileName: !fileName
        ? 'ファイル名を指定してください。'
        : fileName.length > FIRMWARE_FILENAME_LEN_LIMIT
        ? `ファイル名は${FIRMWARE_FILENAME_LEN_LIMIT}文字以内で設定してください。`
        : '',
      memo:
        memo.length > FIRMWARE_MEMO_LEN_LIMIT
          ? `メモは${FIRMWARE_MEMO_LEN_LIMIT}文字以内で設定してください。`
          : '',
    };
    setValidationErrors(errors);
    return !Object.values(errors).some((error) => error !== '');
  };

  const handleClose = (registered: any | null) => {
    // reset useState variables to reset the view on next time opened
    setValidationErrors({
      fileData: '',
      fileName: '',
      memo: '',
    });
    setFileData('');
    setFileName('');
    setMemo('');

    // close this modal
    props.close(registered);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }
    setIsFileLoadInProgress(true);

    // acquire target file Blob and its name
    const file = e.target.files[0];
    const { name } = file;

    // pass Blob to file reader to generate Base64 string
    const reader = new FileReader();
    reader.onloadend = (pe) => {
      setIsFileLoadInProgress(false);
      if (!pe || !pe.target) {
        console.error('onloadend: event or event.target is null');
        return;
      }
      if (typeof pe.target.result !== 'string') {
        console.error('onloadend: event.target.result is not string');
        return;
      }

      // strip prefix of DataURL, which leaves base64 part
      const b64Str = pe.target.result.replace(
        /^data:[\w\-_]*\/[\w\-_]*;base64,/,
        ''
      );
      if (b64Str.length > FIRMWARE_FILEDATA_LEN_LIMIT) {
        // file too large
        setValidationErrors({
          ...validationErrors,
          fileData: `${FIRMWARE_FILEDATA_LEN_LIMIT_TEXT} 以下のファイルを指定してください。`,
        });
        return;
      }

      // save as useState variables
      setFileData(b64Str);
      setFileName(name);

      // ...also, reset the error message
      setValidationErrors({ ...validationErrors, fileData: '' });
    };
    reader.onerror = (pe) => {
      setIsFileLoadInProgress(false);
      console.error(pe.target?.error);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    // check validation result (applies to helper texts), return if NG
    if (!handleValidation()) {
      return;
    }

    setIsSubmitInProgress(true);

    await getJwtTokenFromIdToken()
      .then((token) => {
        // call storeFirmwareBinaryFile API
        return fetch(`${process.env.REACT_APP_API_ENDPOINT}/firmwares`, {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({
            fileName: fileName,
            content: fileData,
            memo: memo,
          }),
        });
      })
      .then((response) => {
        if (!response.ok) {
          console.error(`HTTP error! status: ${response.status}`);
          props.openSnackBarWithError(fetchErrorText);
          return null;
        }
        return response.json();
      })
      .then((body) => {
        if (!body) {
          handleClose(null);
        } else {
          handleClose(body);
        }
        props.openSnackBarWithSuccess(SNACKBAR_TEXT_REGISTER_SUCCESS);
      })
      .catch((e) => {
        props.openSnackBarWithError(fetchErrorText);
        console.error(e);
      })
      .finally(() => {
        setIsSubmitInProgress(false);
      });
  };

  return (
    <>
      <Dialog
        open={props.open}
        onClose={() => handleClose(null)}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">
          新規ファームウェアファイル登録
        </DialogTitle>
        <DialogContent>
          <Stack
            direction="column"
            alignItems="center"
            sx={{ justifyContent: 'center', px: 5, py: 10 }}
          >
            <input
              ref={uploadInputRef}
              type="file"
              hidden
              onChange={handleFileSelect}
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="outlined"
                disabled={isFileLoadInProgress}
                startIcon={isFileLoadInProgress ? null : <UploadFile />}
                onClick={() =>
                  uploadInputRef.current && uploadInputRef.current.click()
                }
              >
                {isFileLoadInProgress ? (
                  <CircularProgress size={30} />
                ) : (
                  'アップロード…'
                )}
              </Button>
              {fileData.length > 0 && (
                <Task color="success" fontSize="large" sx={{ ml: 1 }} />
              )}
            </Box>
            <FormHelperText
              error={!!validationErrors.fileData}
              sx={{ textAlign: 'center' }}
            >
              {validationErrors.fileData}
            </FormHelperText>
          </Stack>
          <TextField
            margin="dense"
            id="fileName"
            label="ファイル名"
            type="text"
            fullWidth
            value={fileName}
            onChange={(e) => {
              setFileName(e.target.value);
            }}
            error={!!validationErrors.fileName}
            helperText={validationErrors.fileName}
          />
          <TextField
            margin="dense"
            id="memo"
            label="メモ"
            type="text"
            fullWidth
            value={memo}
            onChange={(e) => {
              setMemo(e.target.value);
            }}
            error={!!validationErrors.memo}
            helperText={validationErrors.memo}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose(null)} color="primary">
            キャンセル
          </Button>
          <Button onClick={handleSubmit} color="primary">
            {isSubmitInProgress ? <CircularProgress size={20} /> : '登録'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FirmwareUploadModal;
